import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo
} from 'react';

const CacheContext = createContext<CacheContextProps | null>(null);

export function useCache() {
  const context = useContext(CacheContext);

  if (context === null) {
    throw new Error(`useCache should be used within CacheProvider`);
  }

  return context;
}

type CacheContextProps = {
  getCache(key: string, cacheTime: number): Promise<unknown>;
  setCache(key: string, result: unknown): Promise<unknown>;
  removeCache(key: string): Promise<unknown>;
};

type CacheProviderProps = PropsWithChildren<{
  prefix: string;
}>;

export function CacheProvider({ children, prefix }: CacheProviderProps) {
  const value = useMemo(() => getCacheApi(`${prefix}Cache`), [prefix]);

  return (
    <CacheContext.Provider value={value}>{children}</CacheContext.Provider>
  );
}

function getCacheApi(dbName: string) {
  const openDB = () => {
    return new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(dbName, 1);

      request.onupgradeneeded = () => {
        const { result } = request;

        result
          .createObjectStore('Items', {
            keyPath: 'key'
          })
          .createIndex('key', 'key', { unique: true });
      };

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  };

  const setCache = async (key: string, result: unknown) => {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['Items'], 'readwrite');

      const value = {
        key,
        result,
        date: Math.floor(Date.now() / 1000)
      };

      const request = transaction.objectStore('Items').put(value);

      request.onsuccess = () => {
        const { result } = request;

        resolve(result);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  };

  const removeCache = async (key: string) => {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['Items'], 'readwrite');
      const request = transaction.objectStore('Items').delete(key);

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  };

  const getCache = async (key: string, cacheTime: number) => {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['Items'], 'readonly');
      const request = transaction.objectStore('Items').get(key);

      request.onsuccess = () => {
        const { result } = request;

        if (result && result.date + cacheTime > Math.floor(Date.now() / 1000)) {
          resolve(result.result);
        } else {
          removeCache(key);
          reject();
        }
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  };

  return { getCache, setCache, removeCache };
}
